import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Facebook, Instagram, Mail, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const PACKAGE_OPTIONS = [
  "Mini Game — $50",
  "Game Day — $85",
  "Full Game — $125",
  "Player Package — $100",
  "Team Package — $200–$300",
  "Tournament Package — $350–$500+",
  "Not sure yet",
];

// Tap-friendly game-time slots — includes escape hatches so every family
// can submit (no more native time-picker struggles).
const TIME_OPTIONS = [
  "Not sure yet",
  "Multiple games (tournament)",
  ...[
    "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
    "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
    "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM",
    "8:00 PM", "8:30 PM", "9:00 PM",
  ],
];

const INPUT_CLASS =
  "w-full rounded-lg border border-white/10 bg-surface px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors duration-200 focus:border-accentlight focus:bg-surface-hover";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    package: PACKAGE_OPTIONS[0],
    game_date: "",
    team: "",
    league: "",
    field_location: "",
    game_time: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  // Tournaments: one time slot per picked day, e.g. {"2026-11-13": "9:00 AM"}
  const [dayTimes, setDayTimes] = useState({});

  useEffect(() => {
    const onPicked = (e) => {
      const val = e.detail || "";
      setForm((f) => ({ ...f, game_date: val }));
      if (val.includes(",")) {
        const days = val.split(",").map((s) => s.trim()).filter(Boolean);
        setDayTimes(Object.fromEntries(days.map((d) => [d, "Not sure yet"])));
      } else {
        setDayTimes({});
      }
    };
    window.addEventListener("gamelens:picked-date", onPicked);
    return () => window.removeEventListener("gamelens:picked-date", onPicked);
  }, []);

  useEffect(() => {
    // "Book again" from My Games pre-fills everything except the new date.
    const raw = sessionStorage.getItem("gl_rebook");
    if (!raw) return;
    sessionStorage.removeItem("gl_rebook");
    try {
      const d = JSON.parse(raw);
      setForm((f) => ({
        ...f,
        name: d.name || f.name,
        email: d.email || f.email,
        phone: d.phone || f.phone,
        package: PACKAGE_OPTIONS.includes(d.package) ? d.package : f.package,
        team: d.team || f.team,
        league: d.league || f.league,
        field_location: d.field_location || f.field_location,
        // Stored times from older bookings (e.g. "18:00") may not be select
        // options — fall back to "Not sure yet" rather than an invalid value.
        game_time: TIME_OPTIONS.includes(d.game_time) ? d.game_time : "Not sure yet",
      }));
    } catch {
      /* bad payload — ignore */
    }
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const isMultiDay = form.game_date.includes(",");
  const days = form.game_date
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const fmtDay = (d) =>
    new Date(`${d}T12:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    // One slot per day for tournaments: "2026-11-13 9:00 AM; 2026-11-14 1:00 PM"
    const payload = { ...form };
    if (isMultiDay) {
      payload.game_time = days
        .map((d) => `${d} ${dayTimes[d] || "Not sure yet"}`)
        .join("; ");
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, payload);
      setStatus("success");
    } catch (err) {
      // Surface the server's status/detail in the console so live-site
      // submission failures can be diagnosed from the customer's browser.
      console.error(
        "Booking submission failed:",
        err?.response?.status,
        err?.response?.data || err?.message
      );
      setStatus("error");
    }
  };

  const reset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      package: PACKAGE_OPTIONS[0],
      game_date: "",
      team: "",
      league: "",
      field_location: "",
      game_time: "",
      message: "",
    });
    setDayTimes({});
    setStatus("idle");
  };

  return (
    <section id="booking" className="relative bg-surface/40 py-24 md:py-36">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-6 md:px-10 lg:grid-cols-2 lg:gap-20">
        <div>
          <SectionHeading
            eyebrow="Book a game"
            title={
              <>
                LET&rsquo;S CAPTURE
                <br />
                <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                  YOUR NEXT GAME
                </span>
              </>
            }
          />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/70">
              Tell us when and where your player or team takes the field. We&rsquo;ll
              confirm availability and lock in your coverage — the earlier the
              heads-up, the better the frames.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10 space-y-4">
              <a
                href="https://instagram.com/gamelensphotos"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-instagram-link"
                className="flex items-center gap-4 rounded-xl border border-white/5 bg-surface p-5 transition-colors duration-300 hover:border-accentlight/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-hover text-accentlight">
                  <Instagram className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-white/40">Instagram</span>
                  <span className="block font-semibold text-white">@gamelensphotos</span>
                </span>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61594933152311"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-facebook-link"
                className="flex items-center gap-4 rounded-xl border border-white/5 bg-surface p-5 transition-colors duration-300 hover:border-accentlight/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-hover text-accentlight">
                  <Facebook className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-white/40">Facebook</span>
                  <span className="block font-semibold text-white">GameLens Photography</span>
                </span>
              </a>
              <a
                href="mailto:GameLensPhotos@gmail.com"
                data-testid="booking-email-link"
                className="flex items-center gap-4 rounded-xl border border-white/5 bg-surface p-5 transition-colors duration-300 hover:border-accentlight/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-hover text-accentlight">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-white/40">Email</span>
                  <span className="block font-semibold text-white">GameLensPhotos@gmail.com</span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          {status === "success" ? (
            <div
              data-testid="booking-success-message"
              className="flex h-full min-h-[420px] flex-col items-center justify-center gap-6 rounded-2xl border border-accentlight/30 bg-surface p-10 text-center"
            >
              <CheckCircle2 className="h-16 w-16 text-accentlight" strokeWidth={1.5} />
              <h3 className="font-display text-4xl tracking-wide">Booking received</h3>
              <p className="max-w-sm text-sm leading-relaxed text-white/60">
                Thanks {form.name.split(" ")[0] || "there"} — your request is in.
                We&rsquo;ll reply to {form.email} shortly to confirm your game.
              </p>
              <button
                onClick={reset}
                data-testid="booking-reset-button"
                className="rounded-full border border-accentlight/40 px-6 py-3 text-sm font-bold uppercase tracking-wider text-accentlight transition-colors hover:bg-accentlight/10"
              >
                Book another game
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="space-y-5 rounded-2xl border border-white/5 bg-ink p-8 md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="bk-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    Name *
                  </label>
                  <input
                    id="bk-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    data-testid="booking-name-input"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label htmlFor="bk-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    Email *
                  </label>
                  <input
                    id="bk-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    data-testid="booking-email-input"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="bk-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    Phone
                  </label>
                  <input
                    id="bk-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(555) 000-0000"
                    data-testid="booking-phone-input"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label htmlFor="bk-date" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    Game date
                  </label>
                  <input
                    id="bk-date"
                    type={form.game_date.includes(",") ? "text" : "date"}
                    style={{ colorScheme: "dark" }}
                    value={form.game_date}
                    onChange={set("game_date")}
                    data-testid="booking-date-input"
                    className={INPUT_CLASS}
                  />
                  {form.game_date && !form.game_date.includes(",") && (
                    <p className="mt-2 text-[11px] text-accentlight/80" data-testid="booking-date-hint">
                      Date picked from the calendar — adjust here if needed.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="bk-package" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  Package
                </label>
                <select
                  id="bk-package"
                  style={{ colorScheme: "dark" }}
                  value={form.package}
                  onChange={set("package")}
                  data-testid="booking-package-select"
                  className={`${INPUT_CLASS} appearance-none`}
                >
                  {PACKAGE_OPTIONS.map((p) => (
                    <option key={p} value={p} className="bg-surface">
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="bk-team" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  Team / player name *
                </label>
                <input
                  id="bk-team"
                  required
                  value={form.team}
                  onChange={set("team")}
                  placeholder="e.g. Riverside Ravens U12"
                  data-testid="booking-team-input"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="bk-league" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  League <span className="text-white/30">(optional)</span>
                </label>
                <input
                  id="bk-league"
                  value={form.league}
                  onChange={set("league")}
                  placeholder="e.g. County Youth League"
                  data-testid="booking-league-input"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="bk-location" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  Field / location <span className="text-white/30">(optional)</span>
                </label>
                <input
                  id="bk-location"
                  value={form.field_location}
                  onChange={set("field_location")}
                  placeholder="e.g. Riverside Field, diamond 3"
                  data-testid="booking-location-input"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="bk-time" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                  {isMultiDay ? "Game time per day" : "Game time *"}
                </label>
                {isMultiDay ? (
                  <div className="space-y-3" data-testid="booking-multi-time">
                    {days.map((d) => (
                      <div key={d}>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-white/40">
                          {fmtDay(d)}
                        </label>
                        <select
                          style={{ colorScheme: "dark" }}
                          value={dayTimes[d] || "Not sure yet"}
                          onChange={(e) => setDayTimes((t) => ({ ...t, [d]: e.target.value }))}
                          data-testid={`booking-time-${d}`}
                          className={INPUT_CLASS}
                        >
                          {TIME_OPTIONS.map((t) => (
                            <option key={t} value={t} className="bg-surface">
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                    <p className="text-[11px] text-accentlight/80" data-testid="booking-date-multi-hint">
                      One slot per day — leave &ldquo;Not sure yet&rdquo; and times can be firmed up later.
                    </p>
                  </div>
                ) : (
                  <select
                    id="bk-time"
                    style={{ colorScheme: "dark" }}
                    required
                    value={form.game_time}
                    onChange={set("game_time")}
                    data-testid="booking-time-input"
                    className={INPUT_CLASS}
                  >
                  <option value="" disabled>
                    Pick the start time…
                  </option>
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  </select>
                )}
              </div>
              </div>

            <div>
              <label htmlFor="bk-notes" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                Any other notes
              </label>
              <textarea
                id="bk-notes"
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="Anything else we should know — jerseys, photo requests, nothing at all…"
                data-testid="booking-notes-input"
                className={`${INPUT_CLASS} resize-none`}
              />
            </div>

              {status === "error" && (
                <p
                  data-testid="booking-error-message"
                  className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                >
                  Something went wrong sending your booking — please try again or
                  email GameLensPhotos@gmail.com directly.
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="booking-submit-button"
                className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-accent to-accentlight py-4 text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send booking request"
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
